import { Z } from "aldoo-ra/ZLayers"
import React, { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import ColorPicker from "aldoo-ra/HtmlEditor/StyleClassEditor/components/ColorPicker"

// Approximate rendered dimensions of the ColorPicker panel (px)
const PICKER_HEIGHT = 340
const PICKER_WIDTH = 380

export default function ColorPickerEditor({
  onChange,
  value,
  field,
  readOnly = false,
}) {
  const [open, setOpen] = useState(false)
  // Portal-positioned popover coords (in viewport space, fixed-positioned).
  // Using a portal keeps the picker on top of any modal / overflow container
  // that would otherwise clip the absolutely-positioned dropdown.
  const [coords, setCoords] = useState({ top: 0, left: 0, openAbove: false, maxWidth: undefined })
  const [pendingColor, setPendingColor] = useState(value || "")
  const containerRef = useRef(null)
  const buttonRef = useRef(null)
  const popoverRef = useRef(null)

  useEffect(() => {
    setPendingColor(value || "")
  }, [value])

  const checkPosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const MARGIN = 8

    const spaceBelow = vh - rect.bottom
    const openAbove = spaceBelow < PICKER_HEIGHT

    const rawTop = openAbove ? rect.top - PICKER_HEIGHT - 4 : rect.bottom + 4
    const top = Math.min(Math.max(rawTop, MARGIN), vh - PICKER_HEIGHT - MARGIN)

    const availableWidth = vw - MARGIN * 2
    const pickerW = Math.min(PICKER_WIDTH, availableWidth)
    const rawLeft = rect.left
    const left = Math.min(Math.max(rawLeft, MARGIN), vw - pickerW - MARGIN)

    setCoords({
      top,
      left,
      openAbove,
      maxWidth: availableWidth,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    checkPosition()
    // Portal popover isn't a DOM child of containerRef, so outside-click also
    // checks the popover ref directly.
    const onPointerDown = (e) => {
      const insideTrigger = containerRef.current && containerRef.current.contains(e.target)
      const insidePopover = popoverRef.current && popoverRef.current.contains(e.target)
      if (!insideTrigger && !insidePopover) setOpen(false)
    }
    const onReposition = () => checkPosition()
    document.addEventListener("pointerdown", onPointerDown)
    window.addEventListener("scroll", onReposition, true)
    window.addEventListener("resize", onReposition)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("scroll", onReposition, true)
      window.removeEventListener("resize", onReposition)
    }
  }, [open, checkPosition])

  const handleToggle = () => {
    if (readOnly) return
    setOpen((o) => !o)
  }

  const handleSelect = (color) => {
    onChange?.(color)
    setOpen(false)
  }

  const handleCancel = () => {
    setPendingColor(value || "")
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        disabled={readOnly}
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-bordered bg-canvas hover:border-primary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className="w-5 h-5 rounded border border-black/20 flex-shrink-0"
          style={{ backgroundColor: pendingColor || "#000000" }}
        />
        <span className="text-text text-sm font-mono">
          {pendingColor || "—"}
        </span>
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={popoverRef}
          className="fixed"
          style={{ top: coords.top, left: coords.left, maxWidth: coords.maxWidth, zIndex: Z.system + 1 }}
        >
          <ColorPicker
            initialColor={pendingColor || "#000000"}
            alpha={false}
            showFormatToggle={true}
            showSwatches={true}
            // Emit every adjustment so live previews (e.g. the theme editor)
            // update as the user drags / types. The explicit select still
            // closes the popover.
            onChange={(c) => { setPendingColor(c); onChange?.(c) }}
            onColorSelect={handleSelect}
            onCancel={handleCancel}
            onClose={handleCancel}
          />
        </div>,
        document.body
      )}
    </div>
  )
}
